// RBAC Permission Hook

import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import type { PermissionKey, EnhancedRole, UserClientAssignment, Client } from '@/lib/security/types';

interface PermissionsState {
  permissions: PermissionKey[];
  role: EnhancedRole | null;
  clientId: string | null;
  client: Client | null;
  assignments: UserClientAssignment[];
  loading: boolean;
  error: string | null;
}

export function usePermissions() {
  const [state, setState] = useState<PermissionsState>({
    permissions: [],
    role: null,
    clientId: null,
    client: null,
    assignments: [],
    loading: true,
    error: null,
  });

  useEffect(() => {
    loadPermissions();
  }, []);

  const loadPermissions = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        setState(prev => ({ ...prev, loading: false }));
        return;
      }

      // Fetch user's client assignments with client details
      const { data: assignments, error: assignError } = await supabase
        .from('user_client_assignments')
        .select(`
          *,
          client:clients(*)
        `)
        .eq('user_id', user.id);

      if (assignError) throw assignError;

      // Get primary assignment
      const primary = assignments?.find(a => a.is_primary) || assignments?.[0];
      
      if (!primary) {
        // Check for legacy user_roles table for backwards compatibility
        const { data: legacyRole } = await supabase
          .from('user_roles')
          .select('role')
          .eq('user_id', user.id)
          .maybeSingle();

        if (legacyRole) {
          // Map legacy role to new system
          const mappedRole = mapLegacyRole(legacyRole.role);
          const permissions = await getPermissionsForRole(mappedRole);
          
          setState({
            permissions,
            role: mappedRole,
            clientId: null,
            client: null,
            assignments: [],
            loading: false,
            error: null,
          });
          return;
        }

        setState(prev => ({ ...prev, loading: false }));
        return;
      }

      // Get permissions for the role
      const permissions = await getPermissionsForRole(primary.role as EnhancedRole);

      setState({
        permissions,
        role: primary.role as EnhancedRole,
        clientId: primary.client_id,
        client: primary.client as Client || null,
        assignments: assignments as UserClientAssignment[] || [],
        loading: false,
        error: null,
      });
    } catch (err) {
      console.error('Failed to load permissions:', err);
      setState(prev => ({
        ...prev,
        loading: false,
        error: String(err),
      }));
    }
  };

  /**
   * Check if user has a specific permission
   */
  const hasPermission = useCallback((permission: PermissionKey): boolean => {
    return state.permissions.includes(permission);
  }, [state.permissions]);

  /**
   * Check if user has any of the given permissions
   */
  const hasAnyPermission = useCallback((permissions: PermissionKey[]): boolean => {
    return permissions.some(p => state.permissions.includes(p));
  }, [state.permissions]);

  /**
   * Check if user has all of the given permissions
   */
  const hasAllPermissions = useCallback((permissions: PermissionKey[]): boolean => {
    return permissions.every(p => state.permissions.includes(p));
  }, [state.permissions]);

  /**
   * Check if user is a super admin
   */
  const isSuperAdmin = useCallback((): boolean => {
    return state.role === 'super_admin';
  }, [state.role]);

  /**
   * Check if user can access a specific client
   */
  const canAccessClient = useCallback((clientId: string): boolean => {
    if (isSuperAdmin()) return true;
    return state.assignments.some(a => a.client_id === clientId);
  }, [state.assignments, isSuperAdmin]);

  /**
   * Check if user can view sensitive data
   */
  const canViewSensitiveData = useCallback((): boolean => {
    return hasAnyPermission(['sensitive.raw_payloads', 'sensitive.payer_responses', 'sensitive.full_phi']);
  }, [hasAnyPermission]);

  /**
   * Check if PHI should be masked for this user
   */
  const shouldMaskPHI = useCallback((): boolean => {
    return !hasPermission('sensitive.full_phi');
  }, [hasPermission]);

  return {
    ...state,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    isSuperAdmin,
    canAccessClient,
    canViewSensitiveData,
    shouldMaskPHI,
    refresh: loadPermissions,
  };
}

/**
 * Map legacy app_role to new EnhancedRole
 */
function mapLegacyRole(legacyRole: string): EnhancedRole {
  switch (legacyRole) {
    case 'surgeon':
      return 'surgeon';
    case 'link_ops':
      return 'link_ops';
    case 'admin':
      return 'link_ops_manager';
    default:
      return 'surgeon';
  }
}

/**
 * Get permissions for a role from the database
 */
async function getPermissionsForRole(role: EnhancedRole): Promise<PermissionKey[]> {
  const { data, error } = await supabase
    .from('role_permissions')
    .select('permission_key')
    .eq('role', role);

  if (error) {
    console.error('Failed to fetch role permissions:', error);
    return [];
  }

  return (data || []).map(rp => rp.permission_key as PermissionKey);
}
